import {autoInjectable, container} from 'tsyringe';
import {
    AmbientLight,
    BoxGeometry,
    CanvasTexture,
    Color,
    CylinderGeometry,
    DirectionalLight,
    DoubleSide,
    Euler,
    ExtrudeGeometry,
    Group,
    MathUtils,
    Mesh,
    MeshBasicMaterial,
    MeshPhongMaterial,
    MeshStandardMaterial,
    Object3D,
    PlaneGeometry,
    Quaternion,
    RepeatWrapping,
    Shape,
    SRGBColorSpace,
    Texture,
    TextureLoader,
    Vector3,
} from 'three';

import robot from '@fonts/Roboto-Regular.ttf';
import {Text} from 'troika-three-text';
import {Body, Box, Material, Quaternion as CannonQuaternion, Vec3} from 'cannon-es';
import VrObject3D from '../../../../Components/Three/VrObject3D';
import CollisionSoundManager from '../../../../Components/CollisionSoundManager';
import Resources from '../../../../Resources';
import IntersectionContainer from '../../../../Components/Controllers/IntersectionContainer';
import XrControllers from '../../../../Components/Controllers/XrControllers';
import VrScene from '../../../../Components/Three/VrScene';
import Camera from '../../../../Camera';
import Time from '../../../../Utils/Time';
import Renderer from '../../../../Components/Renderer';
import {FloorGridMaterialAlt} from '../../../../../shaders/FloorGrid';
import woodTexture from '@assets/textures/polywood/plywood_diff_1k.jpg';
import rubberTexture from '@assets/textures/rubber.jpg';

type BlockMaterial = 'wood' | 'rubber';

interface Block {
    mesh: VrObject3D;
    body: Body;
    material: BlockMaterial;
    isSliding: boolean;

    velocity: number;
    distanceFromStart: number;
    isOnPlane: boolean;
    initialPosition: Vec3;
    localZ?: number;
    criticalAngle: number;
    lastDetachTime: number;
}

@autoInjectable()
export default class StaticFrictionExperiment extends VrScene {
    public name = 'FrictionExperiment';
    public worldModel: Object3D;

    private inclinedPlane: Group | null = null;
    private inclinedPlaneBody: Body | null = null;
    private verticalRuler: VrObject3D | null = null;
    private verticalRulerBody: Body | null = null;
    private horizontalRulerBody: Body | null = null;

    private readonly MIN_RULER_LOCAL_X = 0.898;
    private readonly MAX_RULER_LOCAL_X = 1.25;

    private isRulerBeingGrabbed: boolean = false;

    private grabbedRulerController: Object3D | null = null;
    private rulerGrabOffset: Vector3 = new Vector3();

    private blocks: Block[] = [];
    private currentAngle: number = 0;
    private targetAngle: number = 0;

    private angleDisplay: Text | null = null;
    private protractor: Object3D | null = null;

    private increaseButton: VrObject3D | null = null;
    private decreaseButton: VrObject3D | null = null;
    private resetButton: VrObject3D | null = null;

    private collisionSoundManager: CollisionSoundManager | null = null;

    private readonly CRITICAL_ANGLES = {
        wood_aluminum: [20, 20, 22, 22, 22, 22, 22, 22, 23, 23, 23, 24],
        rubber_aluminum: [30, 30, 31, 31, 32, 32, 32, 32, 32, 33, 34, 34],
    };

    private readonly FRICTION_COEFFICIENTS = {
        wood_aluminum: 0.404,
        rubber_aluminum: 0.625,
    };

    private rulerGrabOffsetLocalX: number = 0;

    private getRandomCriticalAngle(material: BlockMaterial): number {
        const key = `${material}_aluminum`;
        const angles = this.CRITICAL_ANGLES[key];
        const randomIndex = Math.floor(Math.random() * angles.length);
        return angles[randomIndex];
    }

    private readonly GRAVITY = 9.81;

    private readonly PLANE_LENGTH = 1.2;
    private readonly PLANE_WIDTH = 0.3;
    private readonly RULER_HEIGHT = 0.1;

    private instructionSteps: Text[] = [];
    private currentStep: number = 1;
    private readonly INACTIVE_COLOR = 0xffffff;
    private readonly ACTIVE_COLOR = 0x00ff00;
    private readonly COMPLETED_COLOR = 0x888888;

    public constructor(
        public camera?: Camera,
        public time?: Time,
        public renderer?: Renderer,
        public resources?: Resources,
        public intersectionContainer?: IntersectionContainer,
    ) {
        super();
        this.setWorldModel();
        const xrControllers = container.resolve(XrControllers);
        this.interactionManager.setXrControllers(xrControllers);
    }

    public setWorldModel() {
        this.worldModel = this.resources.items.classScene.scene.clone();
        this.worldModel.position.y = -2.5;
        this.worldModel.traverse((child) => {
            //@ts-ignore
            if (child.isMesh) {
                child.castShadow = true;
                child.receiveShadow = true;
            }
        });
        this.add(this.worldModel);

        const floorGroup = new Object3D();
        floorGroup.name = 'floorGroup';
        const cubeObject: any = this.worldModel.getObjectByName('Cube030');
        const floorBorder: any = this.worldModel.getObjectByName('Cube030_1');
        if (floorBorder) floorBorder.visible = false;
        if (cubeObject) cubeObject.visible = false;

        const cubeTexture = cubeObject ? cubeObject.material.map : null;
        const gridMaterial = new FloorGridMaterialAlt(cubeTexture);
        const mesh = new Mesh(new PlaneGeometry(40, 40, 32), gridMaterial);
        mesh.receiveShadow = true;
        mesh.castShadow = false;
        mesh.name = 'floor';
        mesh.visible = true;
        mesh.position.y = -2.5;
        mesh.quaternion.setFromAxisAngle(new Vector3(1, 0, 0), -Math.PI / 2);
        this.addBoundingBox(mesh, false);
        floorGroup.add(mesh);
        this.add(floorGroup);
    }

    private createTable() {
        const tableGroup = new Object3D();
        const tableMaterial = new MeshPhongMaterial({color: new Color('#9c9c9c')});
        const tableTopSize = new Vector3(2.3, 0.1, 1);
        const legSize = new Vector3(0.1, 0.78, 0.1);
        const tableTopMesh = new Mesh(new BoxGeometry(tableTopSize.x, tableTopSize.y, tableTopSize.z), tableMaterial);
        tableTopMesh.position.y = legSize.y;
        tableTopMesh.castShadow = true;
        tableTopMesh.receiveShadow = true;

        this.interactionManager.addInteractiveObject(tableTopMesh, 10, {});
        tableGroup.add(tableTopMesh);
        const legPositions = [
            new Vector3(tableTopSize.x / 2 - legSize.x, legSize.y / 2, tableTopSize.z / 2 - legSize.z),
            new Vector3(-tableTopSize.x / 2 + legSize.x, legSize.y / 2, tableTopSize.z / 2 - legSize.z),
            new Vector3(tableTopSize.x / 2 - legSize.x, legSize.y / 2, -tableTopSize.z / 2 + legSize.z),
            new Vector3(-tableTopSize.x / 2 + legSize.x, legSize.y / 2, -tableTopSize.z / 2 + legSize.z),
        ];
        legPositions.forEach((pos) => {
            const legMesh = new Mesh(new BoxGeometry(legSize.x, legSize.y, legSize.z), tableMaterial);
            legMesh.position.copy(pos);
            legMesh.castShadow = true;
            legMesh.receiveShadow = true;
            tableGroup.add(legMesh);
        });
        tableGroup.position.set(0, -2.52, 0);
        this.add(tableGroup);
        const tableBody = new Body({mass: 0});
        const tableTopShape = new Box(new Vec3(tableTopSize.x / 2, tableTopSize.y / 2, tableTopSize.z / 2));
        tableBody.addShape(tableTopShape, new Vec3(0, legSize.y, 0));
        const legShape = new Box(new Vec3(legSize.x / 2, legSize.y / 2, legSize.z / 2));
        legPositions.forEach((pos: any) => {
            tableBody.addShape(legShape, pos);
        });
        tableBody.position.copy(tableGroup.position as any);

        //@ts-ignore
        tableBody.userData = {mesh: tableGroup};

        if (this.collisionSoundManager) {
            this.collisionSoundManager.registerBody(tableBody, 'wood', true);
        }
        this.physicsWorld.addBody(tableBody);
    }

    private createInclinedPlane(): void {
        const mainGroup = new Group();
        mainGroup.name = 'InclinedPlaneSetup';
        mainGroup.userData = {isInclinedPlane: true};

        const VERTICAL_RULER_HEIGHT = 1.2;

        const verticalRulerObject = new VrObject3D();
        verticalRulerObject.name = 'VerticalRuler';
        verticalRulerObject.userData = {isVerticalRuler: true};

        const rulerMesh = this.createRuler(
            VERTICAL_RULER_HEIGHT,
            this.PLANE_WIDTH,
            new Color('#88ccff'),
            true,
            0,
            false,
            false,
        );
        rulerMesh.rotation.z = Math.PI / 2;
        rulerMesh.position.y = VERTICAL_RULER_HEIGHT / 2;
        rulerMesh.position.z = -this.PLANE_WIDTH / 2;

        verticalRulerObject.add(rulerMesh);

        verticalRulerObject.position.x = this.PLANE_LENGTH + this.RULER_HEIGHT / 2;
        verticalRulerObject.position.y = 0;
        verticalRulerObject.position.z = 0;

        mainGroup.add(verticalRulerObject);
        this.verticalRuler = verticalRulerObject;

        const horizontalRulerLength = this.MAX_RULER_LOCAL_X + 0.05;
        const horizontalRuler = this.createRuler(
            horizontalRulerLength,
            this.PLANE_WIDTH,
            new Color('#ffdd44'),
            true,
            10,
            false,
            false,
        );
        horizontalRuler.position.set(horizontalRulerLength / 2, -this.RULER_HEIGHT / 2 - 0.005, -this.PLANE_WIDTH / 2);
        mainGroup.add(horizontalRuler);

        const rotatingGroup = new Group();
        rotatingGroup.name = 'RotatingPart';

        const surfaceGeometry = new BoxGeometry(this.PLANE_LENGTH, 0.01, this.PLANE_WIDTH);
        const surfaceMaterial = new MeshStandardMaterial({
            color: 0xc0c0c0,
            metalness: 0.8,
            roughness: 0.2,
        });
        const surface = new Mesh(surfaceGeometry, surfaceMaterial);
        surface.receiveShadow = true;
        surface.castShadow = true;
        surface.userData = {isPlaneSurface: true};
        surface.position.set(this.PLANE_LENGTH / 2, 0, -this.PLANE_WIDTH / 2);
        rotatingGroup.add(surface);

        mainGroup.add(rotatingGroup);
        mainGroup.position.set(-0.5, -1.6, 0);
        this.add(mainGroup);
        this.inclinedPlane = rotatingGroup;

        const verticalRulerBody = this.createRulerCollider(rulerMesh, verticalRulerObject, {
            mass: 0,
            bodyType: 'kinematic',
            showWireframe: false,
            wireframeColor: 0x00ff00,
        });

        //@ts-ignore
        verticalRulerBody.userData = {mesh: verticalRulerObject, rulerMesh: rulerMesh, isRuler: true};
        this.verticalRulerBody = verticalRulerBody;
        this.physicsWorld.addBody(verticalRulerBody);

        if (this.collisionSoundManager) {
            this.collisionSoundManager.registerBody(verticalRulerBody, 'wood', true);
        }

        const horizontalRulerBody = this.createRulerCollider(horizontalRuler, mainGroup, {
            mass: 0,
            bodyType: 'static',
            showWireframe: false,
            wireframeColor: 0xffff00,
        });

        //@ts-ignore
        horizontalRulerBody.userData = {mesh: horizontalRuler, isRuler: true};
        this.horizontalRulerBody = horizontalRulerBody;
        this.physicsWorld.addBody(horizontalRulerBody);

        if (this.collisionSoundManager) {
            this.collisionSoundManager.registerBody(horizontalRulerBody, 'wood', true);
        }

        const planeBody = new Body({
            mass: 0,
            type: Body.STATIC,
        });

        const planeShape = new Box(new Vec3(this.PLANE_LENGTH / 2, 0.005, this.PLANE_WIDTH / 2));
        planeBody.addShape(planeShape);
        planeBody.addShape(planeShape);

        const planeMaterial = new Material('plane');
        planeBody.material = planeMaterial

        if (this.collisionSoundManager) {
            this.collisionSoundManager.registerBody(planeBody, 'metal', true);
        };

        //@ts-ignore
        planeBody.userData = {mesh: rotatingGroup, isPlane: true};

        this.inclinedPlaneBody = planeBody;
        this.physicsWorld.addBody(planeBody);

        this.setupRulerInteraction();
        this.updatePlaneAngle(0);
    }

    private createRulerCollider(
        rulerMesh: Mesh,
        targetObject: Object3D,
        options: {
            mass?: number;
            bodyType?: 'static' | 'kinematic';
            showWireframe?: boolean;
            wireframeColor?: number;
        } = {},
    ): Body {
        const {mass = 0, bodyType = 'kinematic', showWireframe = true, wireframeColor = 0x00ff00} = options;

        const body = new Body({
            mass: mass,
            type: bodyType === 'static' ? Body.STATIC : Body.KINEMATIC,
        });

        if (!(rulerMesh.geometry instanceof BoxGeometry)) {
            return body;
        }

        const geomParams = rulerMesh.geometry.parameters;
        const size = new Vector3(geomParams.width, geomParams.height, geomParams.depth);

        const shape = new Box(new Vec3(size.x / 2, size.y / 2, size.z / 2));
        body.addShape(shape);

        const worldCenter = new Vector3();
        rulerMesh.getWorldPosition(worldCenter);
        body.position.set(worldCenter.x, worldCenter.y, worldCenter.z);

        const worldQuat = new Quaternion();
        rulerMesh.getWorldQuaternion(worldQuat);
        body.quaternion.set(worldQuat.x, worldQuat.y, worldQuat.z, worldQuat.w);

        if (showWireframe) {
            const wireframeGeometry = new BoxGeometry(size.x, size.y, size.z);
            const wireframeMaterial = new MeshBasicMaterial({
                color: wireframeColor,
                wireframe: true,
                transparent: true,
                opacity: 0.6,
                visible: true,
            });
            const wireframeMesh = new Mesh(wireframeGeometry, wireframeMaterial);
            wireframeMesh.userData.isPhysicsColliderWireframe = true;

            wireframeMesh.position.copy(rulerMesh.position);
            wireframeMesh.rotation.copy(rulerMesh.rotation);

            targetObject.add(wireframeMesh);
        }

        return body;
    }

    private updatePlaneAngle(angle: number): void {
        if (!this.inclinedPlane || !this.inclinedPlaneBody) return;

        const rad = (angle * Math.PI) / 180;

        this.inclinedPlane.rotation.z = rad;

        const surfaceOffset = this.PLANE_LENGTH / 2;
        const offsetX = surfaceOffset * Math.cos(rad);
        const offsetY = surfaceOffset * Math.sin(rad);

        const baseX = -0.5;
        const baseY = -1.6;
        const baseZ = -this.PLANE_WIDTH / 2;

        this.inclinedPlaneBody.position.set(baseX + offsetX, baseY + offsetY, baseZ);

        const quat = new CannonQuaternion();
        quat.setFromAxisAngle(new Vec3(0, 0, 1), rad);
        this.inclinedPlaneBody.quaternion.copy(quat);

        if (this.angleDisplay) {
            this.angleDisplay.text = `${Math.round(angle)}°`;
        }

        if (this.protractor) {
            const needle = this.protractor.getObjectByName('AngleNeedle');
            if (needle) {
                needle.rotation.y = -rad;
            }
        }
    }

    private createRuler(
        length: number,
        width: number,
        color: Color,
        showTopMarkings: boolean = false,
        offsetMm: number = 0,
        flipTopMarkings: boolean = false,
        flipSideMarkings: boolean = false,
    ): Mesh {
        const geometry = new BoxGeometry(length, this.RULER_HEIGHT, width);
        const material = new MeshStandardMaterial({
            color: color,
            roughness: 0.3,
            metalness: 0.1,
        });

        const ruler = new Mesh(geometry, material);
        ruler.castShadow = true;
        ruler.receiveShadow = true;
        ruler.userData = {isRuler: true};

        const sideMarkingsTexture = this.createRulerMarkings(
            length,
            flipSideMarkings ? offsetMm : 0,
            flipSideMarkings,
            'side',
        );
        const markingsMaterial = new MeshStandardMaterial({
            map: sideMarkingsTexture,
            transparent: true,
            side: DoubleSide,
        });

        const sideGeometry = new PlaneGeometry(length, this.RULER_HEIGHT);
        const sideMesh = new Mesh(sideGeometry, markingsMaterial);
        sideMesh.userData = {isRulerMarkings: true, location: 'side'};
        sideMesh.position.z = width / 2 + 0.001;
        ruler.add(sideMesh);

        const topGeometry = new PlaneGeometry(length, width);

        if (showTopMarkings) {
            const topMarkingsTexture = this.createRulerMarkings(length, offsetMm, flipTopMarkings, 'top');
            const topMarkingsMaterial = new MeshStandardMaterial({
                map: topMarkingsTexture,
                transparent: true,
                side: DoubleSide,
            });

            topGeometry.scale(1, this.RULER_HEIGHT / width, 1);
            const topMesh = new Mesh(topGeometry, topMarkingsMaterial);
            topMesh.userData = {isRulerMarkings: true, location: 'top'};
            topMesh.position.set(0, this.RULER_HEIGHT / 2 + 0.001, width / 2 - this.RULER_HEIGHT / 2);
            topMesh.rotation.x = -Math.PI / 2;
            ruler.add(topMesh);
        } else {
            const topMaterial = new MeshStandardMaterial({
                color: color,
                roughness: 0.3,
                metalness: 0.1,
                side: DoubleSide,
            });
            const topMesh = new Mesh(topGeometry, topMaterial);
            topMesh.userData = {isRulerMarkings: false, location: 'top'};
            topMesh.position.y = this.RULER_HEIGHT / 2 + 0.001;
            topMesh.rotation.x = -Math.PI / 2;
            ruler.add(topMesh);
        }

        return ruler;
    }

    private calculateAngleFromRulerPosition(rulerX: number): number {
        const planeStartX = -0.5;
        const rulerCenterX = rulerX - planeStartX;
        const rulerThickness = this.RULER_HEIGHT;

        const planeEdgeX = rulerCenterX - rulerThickness / 2;

        const cosAngle = planeEdgeX / this.PLANE_LENGTH;
        const angleRad = Math.acos(Math.max(-1, Math.min(1, cosAngle)));
        const angleDeg = (angleRad * 180) / Math.PI;

        return Math.max(0, Math.min(45, angleDeg));
    }

    private createRulerMarkings(
        length: number,
        offsetMm: number = 0,
        reversed: boolean = false,
        location: 'side' | 'top' = 'side',
    ): CanvasTexture {
        const canvas = document.createElement('canvas');

        canvas.width = 1024 * (length / 1.2);
        canvas.height = 128;
        const ctx = canvas.getContext('2d')!;

        ctx.fillStyle = 'rgba(0, 0, 0, 0)';
        ctx.fillRect(0, 0, canvas.width, canvas.height);

        ctx.strokeStyle = '#000000';
        ctx.fillStyle = '#000000';
        ctx.font = 'bold 24px Arial';
        ctx.textAlign = 'center';

        const cmCount = Math.floor(length * 100);
        const marginPixels = reversed ? (offsetMm / (cmCount + offsetMm)) * canvas.width : 0;
        const effectiveWidth = canvas.width - marginPixels;

        if (location === 'side') {
            ctx.textBaseline = 'top';

            const lineYTop = canvas.height * 0.1;
            const lineYBottomLong = canvas.height * 0.6;
            const lineYBottomMedium = canvas.height * 0.5;
            const lineYBottomShort = canvas.height * 0.4;
            const textYPos = canvas.height * 0.65;

            if (reversed) {
                ctx.textAlign = 'right';
                ctx.fillText('(cm)', canvas.width - 5, textYPos);
            } else {
                ctx.textAlign = 'left';
                ctx.fillText('(cm)', 5, textYPos);
            }

            ctx.textAlign = 'center';
            for (let i = 0; i <= cmCount; i++) {
                if (i % 10 === 0 && i > 0) {
                    const x = reversed
                        ? canvas.width - marginPixels - (i / cmCount) * effectiveWidth
                        : (i / cmCount) * canvas.width;
                    ctx.fillText(`${i / 10}`, x, textYPos);
                }
            }

            for (let i = 0; i <= cmCount; i++) {
                const x = reversed
                    ? canvas.width - marginPixels - (i / cmCount) * effectiveWidth
                    : (i / cmCount) * canvas.width;

                if (i % 10 === 0) {
                    ctx.lineWidth = 3;
                    ctx.beginPath();
                    ctx.moveTo(x, lineYTop);
                    ctx.lineTo(x, lineYBottomLong);
                    ctx.stroke();
                } else if (i % 5 === 0) {
                    ctx.lineWidth = 2;
                    ctx.beginPath();
                    ctx.moveTo(x, lineYTop);
                    ctx.lineTo(x, lineYBottomMedium);
                    ctx.stroke();
                } else {
                    ctx.lineWidth = 1;
                    ctx.beginPath();
                    ctx.moveTo(x, lineYTop);
                    ctx.lineTo(x, lineYBottomShort);
                    ctx.stroke();
                }
            }
        } else {
            ctx.textBaseline = 'bottom';

            const lineYBottom = canvas.height * 0.9;
            const lineYTopLong = canvas.height * 0.4;
            const lineYTopMedium = canvas.height * 0.5;
            const lineYTopShort = canvas.height * 0.6;
            const textYPos = canvas.height * 0.35;

            if (reversed) {
                ctx.textAlign = 'right';
                ctx.fillText('(cm)', canvas.width - 5, textYPos);
            } else {
                ctx.textAlign = 'left';
                ctx.fillText('(cm)', 5, textYPos);
            }

            ctx.textAlign = 'center';
            for (let i = 0; i <= cmCount; i++) {
                if (i % 10 === 0 && i > 0) {
                    const x = reversed
                        ? canvas.width - marginPixels - (i / cmCount) * effectiveWidth
                        : (i / cmCount) * canvas.width;
                    ctx.fillText(`${i / 10}`, x, textYPos);
                }
            }

            for (let i = 0; i <= cmCount; i++) {
                const x = reversed
                    ? canvas.width - marginPixels - (i / cmCount) * effectiveWidth
                    : (i / cmCount) * canvas.width;

                if (i % 10 === 0) {
                    ctx.lineWidth = 3;
                    ctx.beginPath();
                    ctx.moveTo(x, lineYBottom);
                    ctx.lineTo(x, lineYTopLong);
                    ctx.stroke();
                } else if (i % 5 === 0) {
                    ctx.lineWidth = 2;
                    ctx.beginPath();
                    ctx.moveTo(x, lineYBottom);
                    ctx.lineTo(x, lineYTopMedium);
                    ctx.stroke();
                } else {
                    ctx.lineWidth = 1;
                    ctx.beginPath();
                    ctx.moveTo(x, lineYBottom);
                    ctx.lineTo(x, lineYTopShort);
                    ctx.stroke();
                }
            }
        }

        return new CanvasTexture(canvas);
    }

    private setupRulerInteraction(): void {
        if (!this.verticalRuler) return;

        const naturalGrabRotation = new Euler(0, 0, 0);

        this.interactionManager.addGrabbableObject(this.verticalRuler, 'rigid', {
            addChildren: true,
            grabRotation: naturalGrabRotation,
            grabRotationRight: naturalGrabRotation,
            constraints: {
                position: false,
                rotation: true,
            },
            onGrab: (controller: Object3D) => {
                this.isRulerBeingGrabbed = true;
                this.grabbedRulerController = controller;

                if (this.currentStep === 1) {
                    this.currentStep = 2;
                    this.updateInstructionHighlight();
                }
            },
            onRelease: (controller: Object3D) => {
                this.isRulerBeingGrabbed = false;
                this.grabbedRulerController = null;

                const roundedAngle = Math.round(this.targetAngle * 2) / 2;
                this.targetAngle = roundedAngle;
                this.currentAngle = roundedAngle;
                this.updatePlaneAngle(roundedAngle);
            },
            onPositionUpdate: (object: Object3D, proposedPosition: Vector3) => {
                const clampedX = MathUtils.clamp(proposedPosition.x, this.MIN_RULER_LOCAL_X, this.MAX_RULER_LOCAL_X);
                proposedPosition.set(clampedX, 0, 0);
            },
        });
    }

    private async createBlock(material: BlockMaterial, position: Vec3): Promise<void> {
        const block = new VrObject3D();
        block.name = `Block_${material}`;
        block.userData = {isBlock: true, material: material};

        const size = new Vector3(0.15, 0.15, 0.15);
        const geometry = new BoxGeometry(size.x, size.y, size.z);

        let meshMaterial: MeshStandardMaterial;

        // Wczytaj tekstury asynchronicznie
        const textureLoader = new TextureLoader();

        if (material === 'wood') {
            const woodTex = await new Promise<Texture>((resolve, reject) => {
                textureLoader.load(
                    woodTexture,
                    (texture) => {
                        texture.wrapS = RepeatWrapping;
                        texture.wrapT = RepeatWrapping;
                        texture.repeat.set(2, 2);
                        texture.colorSpace = SRGBColorSpace;
                        resolve(texture);
                    },
                    undefined,
                    reject,
                );
            });

            meshMaterial = new MeshStandardMaterial({
                map: woodTex,
                roughness: 0.8,
                metalness: 0.0,
            });
        } else {
            const rubberTex = await new Promise<Texture>((resolve, reject) => {
                textureLoader.load(
                    rubberTexture,
                    (texture) => {
                        texture.wrapS = RepeatWrapping;
                        texture.wrapT = RepeatWrapping;
                        texture.repeat.set(2, 2);
                        resolve(texture);
                    },
                    undefined,
                    reject,
                );
            });

            meshMaterial = new MeshStandardMaterial({
                map: rubberTex,
                roughness: 0.9,
                metalness: 0.0,
            });
        }

        const mesh = new Mesh(geometry, meshMaterial);
        mesh.castShadow = true;
        mesh.receiveShadow = true;
        mesh.userData = {isBlockMesh: true};

        block.add(mesh);
        this.add(block);
        block.position.set(position.x, position.y, position.z);

        const blockBody = new Body({
            mass: 0.5,
            material: new Material(`block_${material}`),
            type: Body.DYNAMIC,
        });

        const blockShape = new Box(new Vec3(size.x / 2, size.y / 2, size.z / 2));
        blockBody.addShape(blockShape);
        blockBody.position.copy(position as any);

        //@ts-ignore
        blockBody.userData = {mesh: block, isBlock: true, material: material};
        this.physicsWorld.addBody(blockBody);
        blockBody.wakeUp();

        block.physicsBody = blockBody;

        const blockObj: Block = {
            mesh: block,
            body: blockBody,
            material: material,
            isSliding: false,
            velocity: 0,
            distanceFromStart: 0,
            isOnPlane: false,
            initialPosition: new Vec3(position.x, position.y, position.z),
            criticalAngle: this.getRandomCriticalAngle(material),
            lastDetachTime: 0,
        };

        this.blocks.push(blockObj);

        blockBody.addEventListener('collide', (event: any) => {
            const otherBody = event.body;

            if (otherBody.userData?.isPlane && !blockObj.isOnPlane) {
                const currentTime = Date.now();
                const timeSinceDetach = (currentTime - blockObj.lastDetachTime) / 1000;

                if (timeSinceDetach > 1.0 && !this.interactionManager.isObjectGrabbed(block)) {
                    this.checkIfBlockOnPlane(blockObj);
                }
            }
        });

        const naturalGrabRotationLeft = new Euler(0, 0, 0);
        const naturalGrabRotationRight = new Euler(0, 0, 0);

        this.interactionManager.addGrabbableObject(block, 'physics', {
            physicsBody: blockBody,
            physicsGripConfig: {
                mode: 'velocity',
                maintainRotation: true,
                maintainVelocity: true,
                velocityStrength: 50,
                angularVelocityStrength: 20,
            },
            addChildren: false,
            grabRotation: naturalGrabRotationLeft,
            grabRotationRight: naturalGrabRotationRight,
            onGrab: () => {
                const blockObj = this.blocks.find((b) => b.body === blockBody);

                if (blockObj && blockObj.isOnPlane) {
                    blockObj.isOnPlane = false;
                    blockObj.isSliding = false;
                    blockObj.velocity = 0;
                    blockObj.lastDetachTime = Date.now();

                    blockBody.type = Body.DYNAMIC;
                    blockBody.collisionResponse = true;

                    blockBody.velocity.set(0, 0, 0);
                    blockBody.angularVelocity.set(0, 0, 0);
                    blockBody.force.set(0, 0, 0);
                    blockBody.torque.set(0, 0, 0);

                    blockBody.wakeUp();
                }

                if (this.currentStep === 1) {
                    this.currentStep = 2;
                    this.updateInstructionHighlight();
                }
            },
            onRelease: () => {
                const blockObj = this.blocks.find((b) => b.body === blockBody);
                if (blockObj && !blockObj.isOnPlane) {
                    this.checkIfBlockOnPlane(blockObj);
                }
            },
        });

        // const label = new Text();
        // label.text = material === 'wood' ? 'DREWNO' : 'GUMA';
        // label.fontSize = 0.02;
        // label.color = 0xffffff;
        // label.anchorX = 'center';
        // label.anchorY = 'middle';
        // label.font = robot;
        // label.position.set(0, size.y / 2 + 0.03, 0);
        // label.material = new MeshPhongMaterial({
        //     color: 0xffffff,
        //     emissive: 0x444444,
        //     side: DoubleSide,
        // });
        // label.userData = { isLabel: true };
        // block.add(label);

        if (this.collisionSoundManager) {
            //@ts-ignore
            this.collisionSoundManager.registerBody(blockBody, material === 'wood' ? 'wood' : 'rubber', true);
        }
    }

    private checkIfBlockOnPlane(block: Block): void {
        const blockPos = block.body.position;
        const planeStartX = -0.5;
        const planeStartY = -1.6;
        const planeStartZ = -this.PLANE_WIDTH / 2;

        const rad = (this.currentAngle * Math.PI) / 180;

        const localX = blockPos.x - planeStartX;
        const localY = blockPos.y - planeStartY;
        const localZ = blockPos.z - planeStartZ;

        const distanceAlongPlane = localX * Math.cos(rad) + localY * Math.sin(rad);
        const distancePerpendicular = -localX * Math.sin(rad) + localY * Math.cos(rad);

        const onPlane =
            Math.abs(distancePerpendicular - 0.075) < 0.12 &&
            distanceAlongPlane >= 0 &&
            distanceAlongPlane <= this.PLANE_LENGTH &&
            Math.abs(localZ) < this.PLANE_WIDTH / 2;

        let isColliding = false;
        if (onPlane) {
            const otherBlocksOnPlane = this.blocks.filter((b) => b !== block && b.isOnPlane);
            isColliding = otherBlocksOnPlane.some((otherBlock) => {
                const blockSize = 0.15;
                const distDiff = Math.abs(distanceAlongPlane - otherBlock.distanceFromStart);
                const zDiff = Math.abs(localZ - otherBlock.localZ);

                return distDiff < blockSize && zDiff < blockSize;
            });
        }

        if (onPlane && !isColliding) {
            block.isOnPlane = true;
            block.distanceFromStart = distanceAlongPlane;
            block.localZ = localZ;
            block.velocity = 0;

            this.snapBlockToPlane(block);
        } else {
            block.isOnPlane = false;
            block.body.type = Body.DYNAMIC;
        }
    }

    private snapBlockToPlane(block: Block): void {
        block.body.type = Body.KINEMATIC;
        block.body.velocity.set(0, 0, 0);
        block.body.angularVelocity.set(0, 0, 0);

        const rad = (this.currentAngle * Math.PI) / 180;
        const distance = block.distanceFromStart;

        const planeStartX = -0.5;
        const planeStartY = -1.6;
        const planeStartZ = -this.PLANE_WIDTH / 2;

        const x = planeStartX + distance * Math.cos(rad);

        const y = planeStartY + distance * Math.sin(rad) + 0.075;

        const z = planeStartZ + (block.localZ || 0);

        block.body.position.set(x, y, z);
        block.mesh.position.set(x, y, z);

        const quat = new CannonQuaternion();
        quat.setFromAxisAngle(new Vec3(0, 0, 1), rad);
        block.body.quaternion.copy(quat);
        block.mesh.quaternion.copy(block.body.quaternion as any);
    }

    private updateBlockPhysics(block: Block, deltaTime: number): void {
        if (!block.isOnPlane) return;

        const rad = (this.currentAngle * Math.PI) / 180;

        const shouldSlide = this.currentAngle >= block.criticalAngle;

        if (shouldSlide) {
            const criticalAngleRad = (block.criticalAngle * Math.PI) / 180;
            const muKinetic = Math.tan(criticalAngleRad) * 0.85;

            const acceleration = this.GRAVITY * (Math.sin(rad) - muKinetic * Math.cos(rad));

            if (acceleration > 0) {
                block.velocity += acceleration * deltaTime;

                block.distanceFromStart -= block.velocity * deltaTime;

                if (block.distanceFromStart <= 0) {
                    block.isOnPlane = false;
                    block.isSliding = false;
                    block.lastDetachTime = Date.now();

                    block.body.type = Body.DYNAMIC;
                    block.body.collisionResponse = true;

                    block.body.velocity.set(-block.velocity * Math.cos(rad), -block.velocity * Math.sin(rad), 0);

                    block.body.wakeUp();
                    return;
                }

                this.snapBlockToPlane(block);

                if (!block.isSliding) {
                    block.isSliding = true;
                    if (this.currentStep === 2) {
                        this.currentStep = 3;
                        this.updateInstructionHighlight();
                    }
                }
            } else {
                block.velocity = 0;
                block.isSliding = false;
                this.snapBlockToPlane(block);
            }
        } else {
            block.velocity = 0;
            block.isSliding = false;
            this.snapBlockToPlane(block);
        }
    }

    private createProtractor(): void {
        const protractorGroup = new Group();
        protractorGroup.name = 'Protractor';
        protractorGroup.userData = {isProtractor: true};

        const arcShape = new Shape();
        const radius = 0.3;
        arcShape.absarc(0, 0, radius, 0, Math.PI / 2, false);
        arcShape.absarc(0, 0, radius - 0.02, Math.PI / 2, 0, true);

        const arcGeometry = new ExtrudeGeometry(arcShape, {
            depth: 0.005,
            bevelEnabled: false,
        });

        const arcMaterial = new MeshStandardMaterial({
            color: 0xffffff,
            transparent: true,
            opacity: 0.8,
            side: DoubleSide,
        });

        const arc = new Mesh(arcGeometry, arcMaterial);
        arc.rotation.x = -Math.PI / 2;
        arc.userData = {isProtractorArc: true};
        protractorGroup.add(arc);

        const markingsMaterial = new MeshStandardMaterial({
            color: 0x000000,
            transparent: true,
            side: DoubleSide,
        });

        for (let angle = 0; angle <= 90; angle += 5) {
            const rad = (angle * Math.PI) / 180;
            const x = Math.cos(rad) * radius;
            const z = Math.sin(rad) * radius;

            const markGeometry = new CylinderGeometry(0.002, 0.002, 0.02, 8);
            const mark = new Mesh(markGeometry, markingsMaterial);
            mark.position.set(x, 0, z);
            mark.rotation.z = Math.PI / 2;
            mark.userData = {isProtractorMark: true};
            protractorGroup.add(mark);

            if (angle % 10 === 0) {
                const label = new Text();
                label.text = `${angle}°`;
                label.fontSize = 0.02;
                label.color = 0x000000;
                label.anchorX = 'center';
                label.anchorY = 'middle';
                label.font = robot;
                label.position.set(x * 1.15, 0.01, z * 1.15);
                label.rotation.x = -Math.PI / 2;
                label.userData = {isProtractorLabel: true};
                protractorGroup.add(label);
            }
        }

        const needleGeometry = new CylinderGeometry(0.003, 0.003, radius, 8);
        const needleMaterial = new MeshStandardMaterial({
            color: 0xff0000,
            emissive: 0xff0000,
            emissiveIntensity: 0.5,
        });
        const needle = new Mesh(needleGeometry, needleMaterial);
        needle.name = 'AngleNeedle';
        needle.position.set(radius / 2, 0, 0);
        needle.rotation.z = Math.PI / 2;
        needle.userData = {isProtractorNeedle: true};
        protractorGroup.add(needle);

        protractorGroup.position.set(-0.6, -0.5, -0.3);
        protractorGroup.rotation.y = Math.PI / 4;
        this.add(protractorGroup);
        this.protractor = protractorGroup;
    }

    private createControlButtons(): void {
        const buttonGroup = new Group();
        buttonGroup.name = 'ControlButtons';
        buttonGroup.position.set(1, -1, 0);
        buttonGroup.rotateY(MathUtils.degToRad(-25));

        this.increaseButton = this.createButton('+', 0x00aa00, new Vector3(0, 0.1, 0));
        if (this.increaseButton) {
            this.increaseButton.userData = {isInteractive: true, isButton: true};
            buttonGroup.add(this.increaseButton);
        }

        this.decreaseButton = this.createButton('-', 0xaa0000, new Vector3(0, 0, 0));
        if (this.decreaseButton) {
            this.decreaseButton.userData = {isInteractive: true, isButton: true};
            buttonGroup.add(this.decreaseButton);
        }

        this.resetButton = this.createButton('RESET', 0x0000aa, new Vector3(0, -0.1, 0));
        if (this.resetButton) {
            this.resetButton.userData = {isInteractive: true, isButton: true};
            buttonGroup.add(this.resetButton);
        }

        this.angleDisplay = new Text();
        this.angleDisplay.text = '0°';
        this.angleDisplay.fontSize = 0.05;
        this.angleDisplay.color = 0xffffff;
        this.angleDisplay.anchorX = 'center';
        this.angleDisplay.anchorY = 'middle';
        this.angleDisplay.font = robot;
        this.angleDisplay.position.set(0, 0.25, 0);
        this.angleDisplay.material = new MeshPhongMaterial({
            color: 0xffffff,
            emissive: 0x444444,
            side: DoubleSide,
        });
        buttonGroup.add(this.angleDisplay);

        const angleLabel = new Text();
        angleLabel.text = 'KĄT POCHYLENIA';
        angleLabel.fontSize = 0.03;
        angleLabel.color = 0xffffff;
        angleLabel.anchorX = 'center';
        angleLabel.anchorY = 'middle';
        angleLabel.font = robot;
        angleLabel.position.set(0, 0.32, 0);
        buttonGroup.add(angleLabel);

        this.add(buttonGroup);
    }

    private createButton(text: string, color: number, position: Vector3): VrObject3D {
        const button = new VrObject3D();
        button.name = `Button_${text}`;
        button.userData = {isButton: true, text: text};

        const geometry = new BoxGeometry(0.15, 0.08, 0.03);
        const material = new MeshStandardMaterial({
            color: color,
            emissive: color,
            emissiveIntensity: 0.3,
        });

        const mesh = new Mesh(geometry, material);
        mesh.castShadow = true;
        mesh.userData = {isButtonMesh: true};
        button.add(mesh);

        const label = new Text();
        label.text = text;
        label.fontSize = 0.04;
        label.color = 0xffffff;
        label.anchorX = 'center';
        label.anchorY = 'middle';
        label.font = robot;
        label.position.set(0, 0, 0.02);
        label.userData = {isLabel: true};
        button.add(label);

        button.position.copy(position);

        return button;
    }

    private createInstructionPanel(): void {
        const panel = new Group();
        panel.name = 'InstructionPanel';
        panel.position.set(-0.7, -1, -0.5);
        panel.rotation.y = Math.PI / 6;
        panel.userData = {isInstructionPanel: true};

        const background = new Mesh(
            new BoxGeometry(0.6, 0.8, 0.02),
            new MeshStandardMaterial({
                color: 0x1a1a1a,
                transparent: true,
                opacity: 0.9,
            }),
        );
        background.userData = {isPanelBackground: true};
        panel.add(background);

        const title = new Text();
        title.text = 'Wyznaczanie współczynnika\ntarcia statycznego';
        title.fontSize = 0.04;
        title.color = 0xffff00;
        title.anchorX = 'center';
        title.anchorY = 'top';
        title.maxWidth = 0.55;
        title.font = robot;
        title.position.set(0, 0.35, 0.02);
        title.userData = {isPanelTitle: true};
        panel.add(title);

        const instructions = [
            '1. Umieść klocek na pochyłej',
            '2. Przesuń niebieską linijkę lub użyj +/-',
            '3. Obserwuj, kiedy klocek zacznie się zsuwać',
            '4. Powtórz dla obu materiałów',
            '5. Porównaj kąty graniczne',
        ];

        let yPos = 0.15;
        instructions.forEach((text, index) => {
            const step = new Text();
            step.text = text;
            step.fontSize = 0.025;
            step.color = this.INACTIVE_COLOR;
            step.anchorX = 'left';
            step.anchorY = 'top';
            step.maxWidth = 0.5;
            step.lineHeight = 1.3;
            step.font = robot;
            step.position.set(-0.25, yPos, 0.02);
            step.userData = {isInstructionStep: true, stepIndex: index};
            panel.add(step);
            this.instructionSteps.push(step);
            yPos -= 0.1;
        });

        this.add(panel);
        this.updateInstructionHighlight();
    }

    private updateInstructionHighlight(): void {
        this.instructionSteps.forEach((step, index) => {
            const stepNum = index + 1;
            if (stepNum < this.currentStep) {
                step.color = this.COMPLETED_COLOR;
            } else if (stepNum === this.currentStep) {
                step.color = this.ACTIVE_COLOR;
            } else {
                step.color = this.INACTIVE_COLOR;
            }
        });
    }

    private changeAngle(delta: number): void {
        this.targetAngle = Math.max(0, Math.min(45, this.targetAngle + delta));

        if (this.currentStep === 1 && delta !== 0) {
            this.currentStep = 2;
            this.updateInstructionHighlight();
        }
    }

    private checkBlockSliding(): void {
        this.blocks.forEach((block) => {
            const velocity = block.body.velocity.length();
            const wasSliding = block.isSliding;
            block.isSliding = velocity > 0.1;

            if (!wasSliding && block.isSliding && this.currentStep === 2) {
                this.currentStep = 3;
                this.updateInstructionHighlight();
            }
        });
    }

    private resetExperiment(): void {
        this.targetAngle = 0;
        this.currentAngle = 0;
        this.updatePlaneAngle(0);

        const woodPos = new Vec3(-0.6, -0.35, 0.1);
        const rubberPos = new Vec3(-0.6, -0.35, -0.1);

        this.blocks.forEach((block) => {
            const pos = block.material === 'wood' ? woodPos : rubberPos;
            block.body.type = Body.DYNAMIC;
            block.body.collisionResponse = true;
            block.body.position.copy(pos);
            block.body.velocity.set(0, 0, 0);
            block.body.angularVelocity.set(0, 0, 0);
            block.body.quaternion.set(0, 0, 0, 1);
            block.isSliding = false;
            block.body.wakeUp();

            block.criticalAngle = this.getRandomCriticalAngle(block.material);
        });

        this.currentStep = 1;
        this.updateInstructionHighlight();
    }

    private initAudioSystem(): void {
        this.collisionSoundManager = new CollisionSoundManager(this.camera.audioListener, this);
    }

    public async start() {
        this.initPhysicsWorld();
        this.initAudioSystem();
        await this.collisionSoundManager.preloadSounds();


        const directionalLight = new DirectionalLight(0xffffff, 2);
        directionalLight.position.set(2, 3, 2);
        directionalLight.castShadow = true;

        const ambientLight = new AmbientLight(0xffffff, 0.6);
        this.add(directionalLight, ambientLight);

        this.createTable();
        this.createInclinedPlane();
        this.createControlButtons();
        this.createInstructionPanel();

        // Wczytaj klocki asynchronicznie
        await this.createBlock('wood', new Vec3(-0.6, -0.35, 0.1));
        await this.createBlock('rubber', new Vec3(-0.6, -0.35, -0.1));


        this.addPhysicsFloor(new Vector3(0, -2.5, 0));

        if (this.collisionSoundManager && this.floorBody) {
            this.collisionSoundManager.registerBody(this.floorBody, 'wood', true);
        }

        if (this.increaseButton) {
            this.interactionManager.addInteractiveObject(this.increaseButton, 0, {
                onSelectStart: () => this.changeAngle(1),
            });
        }

        if (this.decreaseButton) {
            this.interactionManager.addInteractiveObject(this.decreaseButton, 0, {
                onSelectStart: () => this.changeAngle(-1),
            });
        }

        if (this.resetButton) {
            this.interactionManager.addInteractiveObject(this.resetButton, 0, {
                onSelectStart: () => this.resetExperiment(),
            });
        }

        this.interactionManager.setupBVH(this);

        //@ts-ignore
        this.physicsWorld.solver.iterations = 20;
    }

    public update() {
        const deltaTime = this.time.delta / 1000;
        this.physicsWorld.step(1 / 120, deltaTime, 10);

        if (this.verticalRulerBody) {
            //@ts-ignore
            const rulerMesh = this.verticalRulerBody.userData.rulerMesh;
            if (rulerMesh) {
                const meshWorldPos = new Vector3();
                rulerMesh.getWorldPosition(meshWorldPos);
                this.verticalRulerBody.position.set(meshWorldPos.x, meshWorldPos.y, meshWorldPos.z);

                const meshWorldQuat = new Quaternion();
                rulerMesh.getWorldQuaternion(meshWorldQuat);
                this.verticalRulerBody.quaternion.set(
                    meshWorldQuat.x,
                    meshWorldQuat.y,
                    meshWorldQuat.z,
                    meshWorldQuat.w,
                );
            }
            this.verticalRulerBody.wakeUp();
        }
        if (this.verticalRuler) {
            if (this.isRulerBeingGrabbed) {
                const worldPos = new Vector3();
                this.verticalRuler.getWorldPosition(worldPos);
                const newAngle = this.calculateAngleFromRulerPosition(worldPos.x);

                if (Math.abs(newAngle - this.currentAngle) > 0.1) {
                    this.targetAngle = newAngle;
                    this.currentAngle = newAngle;
                    this.updatePlaneAngle(newAngle);

                    this.blocks.forEach((block) => {
                        if (block.isOnPlane) {
                            this.snapBlockToPlane(block);
                        }
                    });
                }
            } else if (Math.abs(this.currentAngle - this.targetAngle) > 0.1) {
                const angleSpeed = 5;
                const angleDelta = (this.targetAngle - this.currentAngle) * angleSpeed * deltaTime;
                this.currentAngle += angleDelta;
                this.updatePlaneAngle(this.currentAngle);

                const rad = this.currentAngle * (Math.PI / 180);

                const planeEdgeX = Math.cos(rad) * this.PLANE_LENGTH;
                const rulerThickness = this.RULER_HEIGHT;
                let newLocalX = planeEdgeX + rulerThickness / 2;

                newLocalX = MathUtils.clamp(newLocalX, this.MIN_RULER_LOCAL_X, this.MAX_RULER_LOCAL_X);

                this.verticalRuler.position.set(newLocalX, 0, 0);

                if (this.verticalRulerBody) {
                    //@ts-ignore
                    const rulerMesh = this.verticalRulerBody.userData.rulerMesh;
                    if (rulerMesh) {
                        const meshWorldPos = new Vector3();
                        rulerMesh.getWorldPosition(meshWorldPos);

                        this.verticalRulerBody.position.set(meshWorldPos.x, meshWorldPos.y, meshWorldPos.z);
                    }

                    const rulerQuat = new CannonQuaternion();
                    rulerQuat.setFromAxisAngle(new Vec3(0, 0, 1), Math.PI / 2);
                    this.verticalRulerBody.quaternion.copy(rulerQuat);
                }

                this.blocks.forEach((block) => {
                    if (block.isOnPlane) {
                        this.snapBlockToPlane(block);
                    }
                });
            }
        }

        this.blocks.forEach((block) => {
            this.updateBlockPhysics(block, deltaTime);
        });

        if (this.horizontalRulerBody) {
            //@ts-ignore
            const horizontalRuler = this.horizontalRulerBody.userData.mesh as Mesh;
            if (horizontalRuler) {
                const worldPos = new Vector3();
                horizontalRuler.getWorldPosition(worldPos);
                this.horizontalRulerBody.position.set(worldPos.x, worldPos.y, worldPos.z);
            }
        }

        for (const body of this.physicsWorld.bodies) {
            //@ts-ignore
            if (body.userData?.mesh && body.type === Body.DYNAMIC) {
                //@ts-ignore
                body.userData.mesh.position.copy(body.position);
                //@ts-ignore
                body.userData.mesh.quaternion.copy(body.quaternion);
            }
        }
    }

    public onLeave() {
        super.onLeave();
        this.resetExperiment();
    }

    public onJoin(state: any): void {
    }

    public destroy() {
        super.destroy();
        this.resetExperiment();
    }
}
